import { composeLayout, defaultDraft } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-layout.js?v=99128771179048044031793722581';
import { appendGlyphs } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-artwork.js?v=113170903786228254451793374472';
import { styles } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-catalog.js?v=20981300605770108511793373704';
import { selectedVariantId } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-offer.js?v=81587650471720582221793722581';

const resources = new Map();
const scenes = {
  classical: { width: 1122, height: 1402, glass: [344, 110, 431, 108] },
  victorian: { width: 1122, height: 1402, glass: [371, 341, 377, 135] },
  brownstone: { width: 1122, height: 1402, glass: [379, 340, 354, 154], center: [556, 435] },
  craftsman: { width: 1024, height: 1536, glass: [318, 301, 399, 96] },
  'art-deco': { width: 1122, height: 1402, glass: [361, 185, 398, 155] },
  modern: { width: 1122, height: 1402, glass: [370, 106, 385, 178] },
};
let sceneSerial = 0;
const resource = url => {
  if (!resources.has(url)) resources.set(url, fetch(url).then(response => {
    if (!response.ok) throw new Error('unavailable');
    return response.json();
  }).catch(error => { resources.delete(url); throw error; }));
  return resources.get(url);
};
const element = (tag, className, text) => {
  const result = document.createElement(tag);
  if (className) result.className = className;
  if (text !== undefined) result.textContent = text;
  return result;
};
const svgElement = (tag, attributes = {}) => {
  const result = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [key, value] of Object.entries(attributes)) result.setAttribute(key, value);
  return result;
};

class HouseStyleGuide extends HTMLElement {
  connectedCallback() {
    if (this.events && !this.events.signal.aborted) return;
    this.events = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-guide-config]').textContent);
    this.copy = this.config.copy;
    this.address = this.querySelector('[name=guide-number]').value;
    this.includePrefix = false;
    this.full = false;
    this.selected = null;
    this.addEventListener('click', event => this.click(event), { signal: this.events.signal });
    this.addEventListener('input', event => {
      if (event.target.name !== 'guide-number') return;
      this.address = event.target.value;
      this.render();
    }, { signal: this.events.signal });
    this.addEventListener('change', event => {
      if (event.target.name !== 'guide-prefix') return;
      this.includePrefix = event.target.checked;
      this.render();
    }, { signal: this.events.signal });
    const home = new URL(location.href).searchParams.get('home');
    if (Object.hasOwn(this.config.families, home)) this.chooseHome(home);
  }

  disconnectedCallback() { this.events?.abort(); this.loadRequest = null; }
  find(selector) { return this.querySelector(selector); }
  text(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, value), this.copy[key]);
  }
  get valid() { return /^[0-9]{1,6}$/.test(this.address); }
  get family() { return this.config.families[this.home]; }
  number(style) { return `${this.includePrefix && styles[style].prefix ? 'No. ' : ''}${this.address}`; }

  show(panel) {
    for (const node of this.querySelectorAll('[data-guide-panel]')) node.hidden = node.dataset.guidePanel !== panel;
    for (const node of this.querySelectorAll('[data-progress]')) {
      if (node.dataset.progress === panel) node.setAttribute('aria-current', 'step');
      else node.removeAttribute('aria-current');
    }
    const heading = this.find(`[data-guide-panel=${panel}] h1`);
    heading.focus({ preventScroll: true });
    this.scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  async chooseHome(home) {
    const request = Symbol();
    this.loadRequest = request;
    if (this.home !== home) this.selected = null;
    this.home = home;
    this.libraries = null;
    this.find('[data-guide-family-label]').textContent = `${this.family.name} · ${this.copy.for_glass}`;
    this.find('[data-guide-title]').textContent = this.family.title;
    this.find('[data-guide-body]').textContent = this.family.body;
    this.find('[data-guide-plaques]').hidden = home !== 'modern';
    this.find('[data-guide-loading]').textContent = this.copy.loading;
    this.find('[data-guide-action=retry]').hidden = true;
    this.render();
    this.show('compare');
    try {
      const [libraries, policies] = await Promise.all([
        Promise.all(this.family.products.map(async product => [product.id, await resource(this.config.libraries[product.id])])),
        resource(this.config.policies),
      ]);
      if (!this.isConnected || this.loadRequest !== request) return;
      this.libraries = Object.fromEntries(libraries);
      this.policies = policies;
      this.find('[data-guide-loading]').textContent = '';
      this.render();
    } catch {
      if (!this.isConnected || this.loadRequest !== request) return;
      this.find('[data-guide-loading]').textContent = this.copy.load_error;
      this.find('[data-guide-action=retry]').hidden = false;
    }
  }

  click(event) {
    const target = event.target.closest('button, a');
    if (!target || !this.contains(target)) return;
    if (target.hasAttribute('data-home')) { this.chooseHome(target.dataset.home); return; }
    if (target.hasAttribute('data-guide-customize')) { this.handoff(event); return; }
    if (target.dataset.style) {
      this.selected = target.dataset.style;
      this.render();
      this.find(`[data-style="${this.selected}"]`).focus({ preventScroll: true });
      return;
    }
    const action = target.dataset.guideAction;
    if (action === 'home') { this.loadRequest = null; this.show('home'); }
    else if (action === 'compare') this.show('compare');
    else if (action === 'detail' || action === 'full') {
      this.full = action === 'full';
      this.find('[data-guide-action=detail]').setAttribute('aria-pressed', String(!this.full));
      this.find('[data-guide-action=full]').setAttribute('aria-pressed', String(this.full));
      this.render();
    } else if (action === 'retry') this.chooseHome(this.home);
    else if (action === 'review' && this.selected && this.valid && this.libraries) {
      this.renderChoice();
      this.show('choice');
    }
  }

  scene(style, full) {
    const scene = scenes[this.home];
    const [x, y, width, height] = scene.glass;
    const [cx, cy] = scene.center || [x + width / 2, y + height / 2];
    const draft = { ...defaultDraft(), heightReference: this.dataset.heightReference || 'face', style, address: this.address, prefix: this.includePrefix && styles[style].prefix };
    const layout = composeLayout(draft, this.libraries[style], this.policies).panes[0];
    const scale = Math.min(height * .48 / layout.heightInches, width * .78 / layout.widthInches);
    const id = `hnl-guide-gold-${++sceneSerial}`;
    const svg = svgElement('svg', { class: 'hnl-guide__scene', role: 'img',
      'aria-label': this.text('preview_alt', { style: styles[style].label, number: this.number(style), family: this.family.name }),
      viewBox: full ? `0 0 ${scene.width} ${scene.height}` : `${cx - 280} ${cy - 165} 560 530` });
    const defs = svgElement('defs');
    const pattern = svgElement('pattern', { id, patternUnits: 'objectBoundingBox', patternContentUnits: 'objectBoundingBox', width: 1, height: 1 });
    pattern.append(svgElement('rect', { width: 1, height: 1, fill: '#e8dbb0' }), svgElement('image', { width: 1, height: 1, href: this.config.gold, preserveAspectRatio: 'none', opacity: .96 }));
    defs.append(pattern);
    svg.append(defs, svgElement('image', { width: scene.width, height: scene.height, href: this.family.entrance }));
    if (this.home === 'classical') {
      const clip = svgElement('clipPath', { id: `${id}-glass` });
      clip.append(svgElement('rect', { x, y, width, height }));
      defs.append(clip);
      const reflections = svgElement('g', { 'clip-path': `url(#${id}-glass)`, opacity: .55 });
      reflections.append(svgElement('image', { width: scene.width, height: scene.height, href: this.config.reflections }));
      svg.append(reflections);
    }
    const artwork = svgElement('svg', { x: cx - layout.widthInches * scale / 2, y: cy - layout.heightInches * scale / 2,
      width: layout.widthInches * scale, height: layout.heightInches * scale,
      viewBox: `0 0 ${layout.widthInches} ${layout.heightInches}` });
    appendGlyphs(artwork, layout.placements, this.libraries[style], '22K Gold');
    for (const [index, group] of [...artwork.children].entries()) {
      const parts = this.libraries[style].glyphs[layout.placements[index].glyph].parts;
      [...group.children].forEach((path, i) => path.setAttribute('fill', parts[i].role === 'face' ? `url(#${id})` : '#3c4349'));
    }
    svg.append(artwork);
    return svg;
  }

  render() {
    this.find('[name=guide-number]').setAttribute('aria-invalid', String(!this.valid));
    this.find('[data-guide-error]').textContent = this.valid ? '' : this.copy.error;
    this.find('[data-guide-action=review]').disabled = !this.valid || !this.selected || !this.libraries;
    const cards = this.find('[data-guide-cards]');
    cards.replaceChildren();
    cards.classList.toggle('hnl-guide__cards--full', this.full);
    this.find('[data-guide-selection]').textContent = this.selected ? this.text('selection', { style: styles[this.selected].label }) : this.copy.choose_prompt;
    if (!this.libraries) return;
    for (const product of this.family.products) {
      const article = element('article', `hnl-guide__card${this.selected === product.id ? ' is-selected' : ''}`);
      if (this.valid) article.append(this.scene(product.id, this.full));
      const content = element('div', 'hnl-guide__card-copy');
      const reason = element('p', 'hnl-guide__reason');
      reason.append(element('strong', '', this.copy.why), document.createTextNode(product.reason));
      content.append(element('p', 'hnl-guide__eyebrow', product.character), element('h2', '', styles[product.id].label), element('p', '', product.intro), reason);
      if (this.includePrefix && !styles[product.id].prefix) content.append(element('p', 'hnl-guide__prefix-note', this.text('prefix_unavailable', { style: styles[product.id].label })));
      const button = element('button', 'hnl-guide__secondary', this.text(this.selected === product.id ? 'selected' : 'choose', { style: styles[product.id].label }));
      button.type = 'button';
      button.dataset.style = product.id;
      button.setAttribute('aria-pressed', String(this.selected === product.id));
      content.append(button);
      article.append(content);
      cards.append(article);
    }
  }

  renderChoice() {
    const product = this.family.products.find(item => item.id === this.selected);
    for (const [key, value] of Object.entries({ character: product.character, style: styles[product.id].label, reason: product.reason, number: this.number(product.id), family: this.family.name })) {
      this.find(`[data-guide-chosen-${key}]`).textContent = value;
    }
    this.find('[data-guide-choice]').replaceChildren(this.scene(product.id, true));
    const link = this.find('[data-guide-customize]');
    const url = new URL(`${this.dataset.rootUrl}products/${styles[product.id].handle}`, location.origin);
    url.searchParams.set('view', 'number-studio');
    const variant = selectedVariantId({ ...defaultDraft(), address: this.address,
      prefix: this.includePrefix && styles[product.id].prefix,
      heightReference: this.dataset.heightReference || 'face' }, styles[product.id]);
    if (variant) url.searchParams.set('variant', variant);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    link.href = url.href;
    link.textContent = this.text('customize', { style: styles[product.id].label });
    delete link.dataset.storageFailed;
    this.find('[data-guide-handoff-error]').textContent = '';
  }

  handoff(event) {
    const link = this.find('[data-guide-customize]');
    if (link.dataset.storageFailed) return;
    event.preventDefault();
    try {
      sessionStorage.setItem('hnl-number-studio-draft-v1', JSON.stringify({ ...defaultDraft(), heightReference: this.dataset.heightReference || 'face', address: this.address,
        prefix: this.includePrefix && styles[this.selected].prefix, style: this.selected }));
      location.assign(link.href);
    } catch {
      this.find('[data-guide-handoff-error]').textContent = this.copy.handoff_error;
      link.dataset.storageFailed = 'true';
      link.textContent = this.copy.handoff_fallback;
    }
  }
}

if (!customElements.get('hnl-style-guide')) customElements.define('hnl-style-guide', HouseStyleGuide);

